/* eslint-disable max-lines */
import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import RxOp from 'rxjs-operators';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Toolbar from 'components/Layout/Toolbar';
import LinearProgress from '@material-ui/core/LinearProgress';

import {
  ICriticalityMetrics,
  IHistoryMetrics,
  IScoreMetrics,
  IVentureStatusMetrics,
  statusesDashboard,
} from 'interfaces/dashboard';
import categoryService from 'services/category';
import dashboardService from 'services/dashboard';
import entityService from 'services/entity';
import selectedVentureService from 'services/profile';
import ventureService from 'services/venture';
import CardGraphs from '../Component/Card';
import DocsActives from '../Graphs/Actives';
import DocumentCriticality from '../Graphs/Criticality';
import HistoricStatus from '../Graphs/HistoricStatus';
import ScoreVenture from '../Graphs/Score';
import DocumentStatus from '../Graphs/Status';
import SnapshotPicker from '../Component/SnapshotPicker';
import DialogStatusByDay from '../Component/DialogStatusByDay';
import { useSnapshots } from '../context/SnapshotsContext';

const ViewEntity = () => {
  const params = useParams<{
    ventureId: number;
    entityId: number;
    categoryId: number;
  }>();
  const { getUrlWithSnap, selectedSnapId } = useSnapshots();

  const containerToPrint = React.useRef();
  const [ventureMetrics, setVentureMetrics] = useState<IVentureStatusMetrics>();
  const [critacalityMetrics, setCritacalityMetrics] =
    useState<ICriticalityMetrics[]>();
  const [scoreMetrics, setScoreMetrics] = useState<IScoreMetrics>();
  const [historyMetrics, setHistoryMetrics] = useState<IHistoryMetrics>();
  const [entityTitle, setEntityTitle] = useState<string>();
  const [entityId, setEntityId] = useState<number>();
  const [ventureTitle, setVentureTitle] = useState<string>();
  const [ventureId, setVentureId] = useState<number>();

  const [categoryTitle, setCategoryTitle] = useState<string>();
  const [categoryId, setCategoryId] = useState<number>();
  const queryParams = useMemo(
    () =>
      new URLSearchParams(selectedSnapId ? `?snapshotId=${selectedSnapId}` : ''),
    [selectedSnapId]
  );
  const [isOpenDialogStatusByDay, setIsOpenDialogStatusByDay] =
    useState<boolean>(false);
  const [currentStatusByDay, setCurrentStatusByDay] =
    useState<statusesDashboard>();
  const [date, setDate] = useState<Date | null>(null);

  const handleChangeDate = (date: Date | null) => setDate(date);
  const handleCloseStatusByDay = () => {
    setIsOpenDialogStatusByDay(false);
    setCurrentStatusByDay(undefined);
  };

  const handleOpenStatusByDay = (status: statusesDashboard) => {
    setIsOpenDialogStatusByDay(true);
    setCurrentStatusByDay(status);
  };

  useEffect(() => {
    selectedVentureService
      .getSelectedVenture()
      .pipe(RxOp.logError())
      .subscribe((selectedVenture) => {
        setVentureId(selectedVenture.id);
        setVentureTitle(selectedVenture.name);

        ventureService.getById(selectedVenture.id).pipe(RxOp.logError());
      });
  }, []);

  useEffect(() => {
    if (params.categoryId) {
      categoryService.getById(params.categoryId).subscribe((category) => {
        setCategoryTitle(category.name);
        setCategoryId(category.id);
      });
    }
  }, [params.categoryId]);

  useEffect(() => {
    if (ventureId) {
      entityService
        .fetchEntitiesGraph(ventureId)
        .pipe(
          RxOp.logError(),
          RxOp.filter(({ data }) => Boolean(data)),
          RxOp.map(({ data }) =>
            data.find((i) => i.id === Number(params.entityId))
          )
        )
        .subscribe((entityId) => {
          setEntityTitle(entityId.name);
          setEntityId(entityId.id);

          dashboardService
            .getStatus({
              ventureId,
              categoryId: params.categoryId,
              entityId: entityId.id,
              params: queryParams,
            })
            .pipe(RxOp.logError())
            .subscribe((m) => setVentureMetrics(m));

          dashboardService
            .getCriticality({
              ventureId,
              categoryId: params.categoryId,
              entityId: entityId.id,
              params: queryParams,
            })
            .pipe(RxOp.logError())
            .subscribe((m) => setCritacalityMetrics(m));

          dashboardService
            .getScore({
              ventureId,
              categoryId: params.categoryId,
              entityId: entityId.id,
              params: queryParams,
            })
            .pipe(RxOp.logError())
            .subscribe((m) => setScoreMetrics(m));

          dashboardService
            .getHistory({
              ventureId,
              categoryId: params.categoryId,
              entityId: entityId.id,
              params: queryParams,
            })
            .pipe(RxOp.logError())
            .subscribe((m) => setHistoryMetrics(m));
        });
    }
  }, [params.categoryId, params.entityId, ventureId, queryParams]);

  const { statusByVenture = null } = ventureMetrics || {};
  const totalDocumentsVenture = statusByVenture
    ? statusByVenture.find(
        (i) => i.status === statusesDashboard.ACTIVE_DOCUMENTS
      ).value
    : 0;

  const findStatus = (status: statusesDashboard) => {
    return statusByVenture.find((i) => status === i.status);
  };

  return (
    <>
      <Toolbar
        title="Dashboard"
        containerToPrint={containerToPrint}
        breadcrumbs={[
          {
            title: 'Dashboard',
            href: getUrlWithSnap('/documentation/dashboard'),
          },
          {
            title: ventureTitle,
            href: getUrlWithSnap(
              `/documentation/dashboard/venture/${ventureId}`
            ),
          },
          {
            title: categoryTitle,
            href: getUrlWithSnap(
              `/documentation/dashboard/venture/${ventureId}/category/${categoryId}`
            ),
          },
          {
            title: entityTitle,
            href: getUrlWithSnap(
              `/documentation/dashboard/venture/${ventureId}/category/${categoryId}/entity/${entityId}`
            ),
          },
        ]}
      />
      <DialogStatusByDay
        open={isOpenDialogStatusByDay}
        onClose={handleCloseStatusByDay}
        status={currentStatusByDay}
        date={date}
        ventureId={ventureId}
        entityId={entityId}
        categoryId={categoryId}
      />
      <Container innerRef={containerToPrint} style={{ marginTop: '40px' }}>
        <Card>
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 20,
              paddingBottom: 0,
            }}
          >
            <div>
              <Typography
                variant="h6"
                color="primary"
                style={{ fontWeight: 700 }}
              >
                {ventureTitle}
              </Typography>
              <Typography variant="body1" color="textPrimary">
                {entityTitle}
              </Typography>
            </div>

            <div>
              <SnapshotPicker onChangeDate={handleChangeDate} />
            </div>
          </Box>

          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {!Boolean(
                  ventureMetrics &&
                    critacalityMetrics &&
                    scoreMetrics &&
                    historyMetrics
                ) && <LinearProgress color="secondary" />}
              </Grid>
              <Grid item xs={12} lg={9} xl={10}>
                {statusByVenture && (
                  <CardGraphs bgcolor="#161d42">
                    <Grid container>
                      {statusByVenture
                        .filter(
                          (item) =>
                            item.status !== statusesDashboard.ACTIVE_DOCUMENTS
                        )
                        .map((ds) => (
                          <Grid
                            item
                            key={`document-status-graph-${ds.status}`}
                            xs={12}
                            sm={6}
                            lg={3}
                          >
                            <DocumentStatus
                              metrics={ds}
                              total={totalDocumentsVenture}
                              onClick={() => handleOpenStatusByDay(ds.status)}
                            />
                          </Grid>
                        ))}
                    </Grid>
                  </CardGraphs>
                )}
              </Grid>
              <Grid item xs={12} lg={3} xl={2}>
                <Grid container>
                  {statusByVenture &&
                    findStatus(statusesDashboard.ACTIVE_DOCUMENTS) && (
                      <Grid item xs={12} sm={6} lg={12}>
                        <DocsActives
                          metrics={findStatus(
                            statusesDashboard.ACTIVE_DOCUMENTS
                          )}
                        />
                      </Grid>
                    )}
                  <Grid item xs={12} sm={6} lg={12}>
                    <DocumentCriticality metrics={critacalityMetrics || []} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={9} xl={10}>
                <HistoricStatus
                  metrics={historyMetrics ? historyMetrics.statusByMonth : []}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={3} xl={2}>
                <ScoreVenture metrics={scoreMetrics || { max: 0, score: 0 }} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </>
  );
};
export default ViewEntity;
